import { useFormik } from "formik";
import { FC, useEffect, useState } from "react"
import { SettingMultiMerchants } from "../../../interfaces/Settings";
import { useIntl } from "react-intl";
import useAccessToken from "../../../hooks/AccessToken";
import { useSettingsStore } from "../../../stores/SettingsStore";
import * as Yup from 'yup';
import { SelectOption, SelectOptionMerchantLink } from "../../../interfaces/SelectOption";
import Swal from "sweetalert2";
import clsx from "clsx";
import VirtualizedSelect, { VirtualizedSelectOption } from "../../Select/SelectVirtualized";
import { Toggle } from "rsuite";
import { components } from "react-select";
import { getAllOption, getFilterDateOptions, getSourceFilterDateOptions } from "../../../functions/getOptions";
import { useMerchantStore } from "../../../stores/MerchantStore";
import { saveSettingMerchantLink, updateTemplateSettingMerchant } from "../../../api/SettingCRUD";

interface Props {
    data: SettingMultiMerchants
    dataPipeline: SelectOption[]
    dataMerchant: SelectOption[]
    dataMeeting: SelectOption[]
    optionMerchantLink: SelectOptionMerchantLink[]
}

const PreferenceMerchant: FC<Props> = ({ data, dataPipeline, optionMerchantLink, dataMeeting }) => {
    const intl = useIntl()
    const token = useAccessToken()
    const { settings } = useSettingsStore()
    const { multiMerchants, setMultiMerchants } = useMerchantStore();
    const [isLoading, setIsLoading] = useState(false)

    const allOptionPipeline = getAllOption(settings.pipeline_title, intl)
    const allOptionMeeting = getAllOption(settings.meeting_title, intl)

    const formik = useFormik({
        initialValues: {
            value: data.name,
            leadTemplateIds: data.leadTemplates && data.leadTemplates.length > 0 ? data.leadTemplates.length === dataPipeline.filter(data => data.value !== 'all').length ? allOptionPipeline : data.leadTemplates : allOptionPipeline,
            meetingTemplateIds: data.meetingTemplates && data.meetingTemplates.length > 0 ? data.meetingTemplates.length === dataMeeting.filter(data => data.value !== 'all').length ? allOptionMeeting : data.meetingTemplates : allOptionMeeting,
            merchantLink: data.merchantLink ? data.merchantLink : [],
            isFeatureLabelEnabled: data.isFeatureLabelEnabled ? true : false,
        },
        validationSchema: Yup.object().shape({
            value: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            leadTemplateIds: Yup.array().min(0, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: settings.pipeline_title })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROGRESS" }) })).nullable(),
            meetingTemplateIds: Yup.array().min(0, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE_TITLE' }, { title: settings.meeting_title }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: 'FORM.LABEL.TEMPLATE_TITLE' }, { title: settings.meeting_title }) })).nullable(),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            const merchantTemplateDestination = values.merchantLink.reduce((acc: any, item: SelectOptionMerchantLink) => {
                if (!acc[item.idTemplate!]) {
                    acc[item.idTemplate!] = {
                        listIdCustomField: []
                    };
                }

                acc[item.idTemplate!].listIdCustomField.push(item.value);
                return acc;
            }, {});

            setIsLoading(true)
            try {
                const leadTemplateIds = values.leadTemplateIds.map(item => item?.value).filter((v): v is string => Boolean(v));
                const meetingTemplateIds = values.meetingTemplateIds.map(item => item?.value).filter((v): v is string => Boolean(v));

                const response = await updateTemplateSettingMerchant(data.id, values.value, data.customFields, leadTemplateIds.map((value) => value), token, meetingTemplateIds, values.isFeatureLabelEnabled)
                const response2 = await saveSettingMerchantLink(data.idMerchantLink!, data.id, merchantTemplateDestination, token)
                if (response.data.success && response2.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: intl.formatMessage({ id: "FORM.VALIDATION.SAVE_SUCCESS" }),
                        timer: 2000,
                        timerProgressBar: true
                    })

                    setMultiMerchants(multiMerchants.map(item => item.id === data.id ? { ...response.data.data, label: response.data.data.name, value: response.data.data.id } : item));
                }
            } catch (error: any) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Terjadi kesalahan, mohon coba lagi.';
                Swal.fire({
                    icon: 'error',
                    heightAuto: false,
                    title: errorMessage,
                    confirmButtonText: 'Ok'
                });
            } finally {
                setIsLoading(false)
            }
        }
    })

    const CustomOptionMerchantLink = (props: any) => {
        const merchantData = props.option;
        return (
            <div className="d-flex align-items-center px-3 h-100">
                <span className="align-middle">{merchantData.nameTemplate} / <b>{merchantData.nameCustomField}</b></span>
            </div>
        );
    };

    const CustomMultiValueMerchantLink = (props: any) => {
        return (
            <components.MultiValue {...props}>
                <span>{props.data.nameTemplate} / <b>{props.data.nameCustomField}</b></span>
            </components.MultiValue>
        );
    };

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <div className="card-body p-0">
                <div className="row">
                    <span className="col-md-7 col-lg-9 col-xl-8 d-flex flex-column align-items-start">
                        <span className="fw-bolder fs-5 mb-0">{`${intl.formatMessage({ id: "FORM.LABEL.NAME" })} `}</span>
                        <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_NAME_TEMPLATE" })}</span>
                    </span>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <input
                            {...formik.getFieldProps('value')}
                            className={clsx(
                                'form-control',
                                { 'is-invalid': formik.touched.value && formik.errors.value },
                            )}
                            type='text'
                            name='value'
                        />
                        {formik.touched.value && formik.errors.value && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.value}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_MERCHANT_PIPELINE" }, { title2: settings.pipeline_title, title: settings.merchant_title })}</span>
                            <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_MERCHANT_PIPELINE" }, { title: settings.pipeline_title, title2: settings.merchant_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${settings.pipeline_title}`}
                            options={dataPipeline}
                            value={formik.values.leadTemplateIds as VirtualizedSelectOption[]}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('leadTemplateIds', allOptionPipeline)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions.length > 0 && selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('leadTemplateIds', allOptionPipeline)
                                    } else {
                                        formik.setFieldValue('leadTemplateIds', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            isMulti
                            isSort={false}
                            closeMenuOnSelect={false}
                            onBlur={
                                () => formik.values.leadTemplateIds.length === 0 && formik.setFieldTouched("leadTemplateIds", true)
                            }
                        />
                        {formik.touched.leadTemplateIds && formik.errors.leadTemplateIds && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.leadTemplateIds}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="separator separator-dashed my-6"></div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('isFeatureLabelEnabled', !formik.values.isFeatureLabelEnabled)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_LABEL" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_LABEL" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('isFeatureLabelEnabled')} checked={formik.values.isFeatureLabelEnabled!} onChange={(e) => formik.setFieldValue('isFeatureLabelEnabled', e)} />
                    {formik.touched.isFeatureLabelEnabled && formik.errors.isFeatureLabelEnabled && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.isFeatureLabelEnabled}</span>
                        </div>
                    )}
                </div>

                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_MERCHANT_PIPELINE" }, { title2: settings.meeting_title, title: settings.merchant_title })}</span>
                            <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_MERCHANT_PIPELINE" }, { title: settings.meeting_title, title2: settings.merchant_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            options={dataMeeting}
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FILTER.LABEL.DATABASE' })} ${settings.meeting_title}`}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('meetingTemplateIds', allOptionMeeting)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('meetingTemplateIds', allOptionMeeting)
                                    } else {
                                        formik.setFieldValue('meetingTemplateIds', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            value={formik.values.meetingTemplateIds as VirtualizedSelectOption[]}
                            isMulti
                            isSort={false}
                            closeMenuOnSelect={false}
                        />
                        {formik.touched.meetingTemplateIds && formik.errors.meetingTemplateIds && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.meetingTemplateIds}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_MERCHANT_LINK" })}</span>
                            <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_MERCHANT_LINK" }, { title: settings.merchant_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={intl.formatMessage({ id: "FORM.PLACEHOLDER.CHOOSE_COLUMN" })}
                            options={optionMerchantLink}
                            value={formik.values.merchantLink}
                            onChange={(selectedOptions) => {
                                formik.setFieldValue('merchantLink', selectedOptions)
                            }}
                            isMulti
                            isSort={false}
                            closeMenuOnSelect={false}
                            components={{
                                Option: CustomOptionMerchantLink,
                                MultiValue: CustomMultiValueMerchantLink
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="card-footer d-flex justify-content-end py-6 px-9 mt-6">
                <button type="submit" className={`btn btn-primary ${isLoading ? "disabled" : ""}`} data-kt-indicator={isLoading ? 'on' : 'off'}>
                    <span className="indicator-label">
                        {intl.formatMessage({ id: "FORM.ACTION.SAVE" })}
                    </span>
                    <span className="indicator-progress">
                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                </button>
            </div>
        </form>
    )
}

export { PreferenceMerchant }